export type ServicesCopy = {
  hero: {
    eyebrow: string
    heading: string
    summary: string
    systemLabel: string
    systemNodes: string[]
  }
  services: {
    eyebrow: string
    heading: string
    introduction: string
    items: Array<{
      title: string
      body: string
      outcome: string
      artifact: string
      technologyGuidance: string
      flow: string[]
    }>
  }
  technology: {
    eyebrow: string
    heading: string
    body: string
    principle: string
    criteria: string[]
  }
  engagement: {
    eyebrow: string
    heading: string
    introduction: string
    items: Array<{ title: string; body: string }>
    founderLink: string
  }
  final: {
    eyebrow: string
    heading: string
    body: string
    review: string
    conversation: string
  }
}

export const SERVICES_COPY: Record<"hr" | "en", ServicesCopy> = {
  hr: {
    hero: {
      eyebrow: "Od web-stranice do poslovnog sustava",
      heading:
        "Gradimo sustave koji povezuju ono što kupci vide s načinom na koji posao stvarno funkcionira.",
      summary:
        "Zmile pomaže etabliranim tvrtkama poboljšati web-stranicu, ukloniti ručne primopredaje i izgraditi pouzdane alate oko stvarnih poslovnih procesa. Početna točka nije tehnologija, nego ograničenje koje usporava ljude ili rast.",
      systemLabel: "Jedan povezani poslovni tok",
      systemNodes: ["Kupac", "Web-stranica", "Radni proces", "Poslovni sustav"],
    },
    services: {
      eyebrow: "Što gradimo",
      heading: "Četiri načina da digitalni dio poslovanja radi bolje.",
      introduction:
        "Suradnja može obuhvatiti jednu jasnu intervenciju ili povezati više dijelova poslovanja. Opseg slijedi problem — ne unaprijed složen paket.",
      items: [
        {
          title: "Web-stranice",
          body:
            "Jasne, vjerodostojne i brze web-stranice za tvrtke koje su prerasle svoju sadašnju digitalnu prisutnost. Povezujemo poruku, sadržaj, korisničko iskustvo i konverzijske putove u cjelinu koja odgovara kvaliteti poslovanja.",
          outcome: "Jasnoća, povjerenje i bolji put do upita.",
          artifact: "Primjer: javna Čajanka web-trgovina i povezani put kupca.",
          technologyGuidance: "AI samo za jasno ograničene zadatke poput pripreme sadržaja; struktura, pristupačnost i put do upita ostaju deterministički.",
          flow: ["Pozicioniranje", "Sadržaj", "Iskustvo", "Upit"],
        },
        {
          title: "Radni procesi i integracije",
          body:
            "Pouzdane veze između web-trgovine, računovodstva, CRM-a, zaliha, internih alata i ljudi. Dobro definirana automatizacija prenosi podatke, uklanja dvostruki unos i čini sljedeći korak vidljivim.",
          outcome: "Manje ručnih primopredaja i manje prostora za pogrešku.",
          artifact: "Primjer: VanCleef povezuje eVisitor, Booking.com uvoze, Novasol i e-račune.",
          technologyGuidance: "Poznata pravila i prijenosi izvode se deterministički; AI ima smisla samo za nestrukturirane ulaze koji ostaju pod ljudskim pregledom.",
          flow: ["Događaj", "Provjera", "Prijenos", "Sljedeći korak"],
        },
        {
          title: "Poslovanje potpomognuto AI-em",
          body:
            "Usmjereni sustavi koji čitaju dokumente, pripremaju strukturirane prijedloge ili pomažu obraditi ponavljajući administrativni rad. Ljudska provjera ostaje tamo gdje su kontekst i odgovornost važni.",
          outcome: "Više korisnih informacija prije ljudske odluke.",
          artifact: "Primjer: Čajankin dobavljački dokument postaje pregledani nacrt ulaza robe.",
          technologyGuidance: "AI priprema prijedlog kada dokument nije uredno strukturiran; provjere, izračuni i knjiženje ostaju deterministički i pregledljivi.",
          flow: ["Dokument", "Prijedlog", "Pregled", "Odluka"],
        },
        {
          title: "Prilagođeni interni sustavi",
          body:
            "Interni alati izrađeni oko načina na koji tvrtka zaista posluje kada generički softver stvara obilazne puteve. Sustav okuplja relevantne podatke, pravila i radne korake u jedno razumljivo iskustvo.",
          outcome: "Alat prilagođen stvarnom procesu, a ne obrnuto.",
          artifact: "Primjer: VanCleefova privatna platforma za kontrolirani računovodstveni tok.",
          technologyGuidance: "AI se dodaje samo kada poboljšava nepredvidiv dio procesa; ovlasti, poslovna pravila, ponovni pokušaji i zapisi ostaju deterministički.",
          flow: ["Stvarni proces", "Pravila", "Alat", "Napredak"],
        },
      ],
    },
    technology: {
      eyebrow: "Stav o tehnologiji",
      heading: "Najjednostavnija tehnologija koja pouzdano rješava problem.",
      body:
        "AI može biti izvrstan kada ulaz nije uredan, kada treba pripremiti informacije ili kada je korisna procjena uz ljudski pregled. Za poznata pravila, ponovljive prijenose i provjerljive ishode deterministička automatizacija često je predvidljivija, lakša za nadzor i jednostavnija za održavanje.",
      principle: "Dobra automatizacija često je bolji odgovor od AI-a.",
      criteria: ["Pouzdan ishod", "Jasna odgovornost", "Razumna složenost"],
    },
    engagement: {
      eyebrow: "Kako suradnja izgleda",
      heading: "Izravan rad, bez sloja između problema i osobe koja ga rješava.",
      introduction:
        "Kao osnivački vođen studio, Zmile drži razgovor, oblikovanje i isporuku povezanim. Ne objavljujemo fiksne cijene jer opseg ovisi o stvarnom ograničenju, postojećim sustavima i odgovornosti koju rad nosi.",
      items: [
        {
          title: "Pokažite ograničenje",
          body:
            "Pošaljite web-stranicu ili opišite dio posla koji je spor, ručan ili nepovezan.",
        },
        {
          title: "Pronađimo koristan početak",
          body:
            "Razdvajamo simptom od uzroka i biramo najmanji korak koji može pokazati stvarnu vrijednost.",
        },
        {
          title: "Učinimo rješenje opipljivim",
          body:
            "Smjer, prototip ili tehnički tok čini odluke vidljivima prije pune izgradnje.",
        },
        {
          title: "Izgradimo i poboljšajmo",
          body:
            "Dogovoreni sustav povezujemo sa stvarnim poslovanjem i usavršavamo prema načinu na koji ga ljudi koriste.",
        },
      ],
      founderLink: "Upoznajte studio",
    },
    final: {
      eyebrow: "Konkretan prvi korak",
      heading: "Počnimo od onoga što danas stvara trenje.",
      body:
        "Web-stranica je dobar ulaz kada želite jasniji pogled izvana. Ako već znate gdje se posao usporava iza kulisa, predložite razgovor.",
      review: "Zatražite besplatne 30-minutne konzultacije",
      conversation: "Pogledajte radove",
    },
  },
  en: {
    hero: {
      eyebrow: "From website to business system",
      heading:
        "We build systems that connect what customers see with how the business actually works.",
      summary:
        "Zmile helps established companies improve their website, remove manual handoffs, and build reliable tools around real operating workflows. The starting point is not a technology—it is the constraint slowing people or growth.",
      systemLabel: "One connected business flow",
      systemNodes: ["Customer", "Website", "Workflow", "Business system"],
    },
    services: {
      eyebrow: "What we build",
      heading: "Four ways to make the digital side of the business work better.",
      introduction:
        "An engagement can address one clear intervention or connect several parts of the business. Scope follows the problem—not a pre-built package.",
      items: [
        {
          title: "Websites",
          body:
            "Clear, credible, and fast websites for companies that have outgrown their current digital presence. We connect positioning, content, user experience, and conversion paths into an experience that reflects the quality of the business.",
          outcome: "Clarity, trust, and a better path to enquiry.",
          artifact: "Example: Čajanka’s public webshop and connected customer journey.",
          technologyGuidance: "Use AI only for tightly scoped work such as content preparation; structure, accessibility, and the enquiry path stay deterministic.",
          flow: ["Positioning", "Content", "Experience", "Enquiry"],
        },
        {
          title: "Workflows and integrations",
          body:
            "Reliable connections between webshops, accounting, CRMs, stock, internal tools, and the people using them. Well-defined automation moves information, removes duplicate entry, and makes the next action visible.",
          outcome: "Fewer manual handoffs and fewer opportunities for error.",
          artifact: "Example: VanCleef connects eVisitor, Booking.com imports, Novasol, and e-računi.",
          technologyGuidance: "Known rules and transfers run deterministically; AI only fits unstructured inputs that remain under human review.",
          flow: ["Event", "Validation", "Transfer", "Next action"],
        },
        {
          title: "AI-assisted operations",
          body:
            "Focused systems that read documents, prepare structured suggestions, or help process repetitive administrative work. Human review stays where context and accountability matter.",
          outcome: "More useful information before a human decision.",
          artifact: "Example: a Čajanka supplier document becomes a reviewed stock-entry draft.",
          technologyGuidance: "AI prepares a suggestion when the document is not structured; validation, calculations, and posting stay deterministic and observable.",
          flow: ["Document", "Suggestion", "Review", "Decision"],
        },
        {
          title: "Custom internal systems",
          body:
            "Internal tools built around how a company really operates when generic software creates workarounds. The system brings relevant information, rules, and working steps into one understandable experience.",
          outcome: "A tool fitted to the real process—not the other way around.",
          artifact: "Example: VanCleef’s private platform for a controlled accounting workflow.",
          technologyGuidance: "Add AI only where it improves an unpredictable part of the process; permissions, business rules, retries, and logs remain deterministic.",
          flow: ["Real process", "Rules", "Tool", "Progress"],
        },
      ],
    },
    technology: {
      eyebrow: "A position on technology",
      heading: "The simplest technology that solves the problem reliably.",
      body:
        "AI can be excellent when inputs are messy, information needs preparing, or a useful judgement can support human review. For known rules, repeatable transfers, and verifiable outcomes, deterministic automation is often more predictable, easier to observe, and simpler to maintain.",
      principle: "Good automation is often the better answer than AI.",
      criteria: ["Reliable outcome", "Clear accountability", "Reasonable complexity"],
    },
    engagement: {
      eyebrow: "How an engagement works",
      heading: "Direct work, with no layer between the problem and the person solving it.",
      introduction:
        "As a Founder-led Studio, Zmile keeps the conversation, shaping, and delivery connected. We do not publish fixed prices because scope depends on the real constraint, the systems already in place, and the responsibility the work carries.",
      items: [
        {
          title: "Show the constraint",
          body:
            "Send your website or describe the part of the work that feels slow, manual, or disconnected.",
        },
        {
          title: "Find the useful start",
          body:
            "We separate the symptom from the cause and choose the smallest step that can demonstrate real value.",
        },
        {
          title: "Make the solution tangible",
          body:
            "A direction, prototype, or technical flow makes decisions visible before a full build.",
        },
        {
          title: "Build and improve",
          body:
            "We connect the agreed system to the real business and refine it around how people use it.",
        },
      ],
      founderLink: "Meet the studio",
    },
    final: {
      eyebrow: "A concrete first step",
      heading: "Start with what creates friction today.",
      body:
        "The website is a useful entry point when you need a clearer outside view. If you already know where work slows down behind the scenes, propose a conversation.",
      review: "Request a free 30-minute consultation",
      conversation: "See the work",
    },
  },
}
